import { inject, observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { ModeJsonModel } from "../models/BlockTransportSystemJsonModel";
import BlockKitStore from "../stores/BlockKitStore";
import ModeInfoCard from "../components/ModeInfoCard/ModeInfoCard";
import ModeSettingBox from "../components/ModeSettingBox/ModeSettingBox";
import ModeSettingBoxWithLicense from "../components/ModeSettingBox/ModeSettingBoxWithLicense";
import { cloneDeep } from "lodash";
import { Badge, Col, Container, ListGroup, Row, Stack, Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";

interface IProps {
  blockKitStore?: BlockKitStore;
}

const ModePage: React.FC<IProps> = inject("blockKitStore")(observer(({
  blockKitStore
}) => {
  const [selectedMode, setSelectedMode] = useState<ModeJsonModel>();
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedLicenseIds, setSelectedLicenseIds] = useState<string[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    blockKitStore?.initModePageAsync();
  }, []);

  return (
    <Container fluid className="p-2" style={{ height: "95dvh", overflowY: "auto" }}>
      <Row>
        <Col md={3} style={{ height: "90dvh", overflowY: "auto" }}>
          <ListGroup as="ul" variant="flush">
            {blockKitStore?.allModes?.map(mode => {
              return (
                <ListGroup.Item
                  key={mode.id}
                  as="li"
                  active={mode.id === selectedMode?.id}
                  onClick={() => setSelectedMode(mode)}>
                  <Stack direction="horizontal">
                    {mode.name}
                    {blockKitStore.publishedModes.some(i => i?.id === mode?.id) &&
                      <Badge bg="info" className="ms-auto">게시중</Badge>}
                  </Stack>
                </ListGroup.Item>
              )
            })
            }
          </ListGroup>
        </Col>
        <Col md={9}>
          <ModeInfoCard
            selectedMode={selectedMode}
            onClickAddMode={async (mode: ModeJsonModel) => {
              await blockKitStore?.studioController.postModesStagingAsync(mode);
              await blockKitStore?.initModePageAsync();
            }}
            onClickCopyMode={async (mode: ModeJsonModel) => {
              if (!mode.name) {
                alert("모드명을 입력해주세요");
                return;
              }

              if (blockKitStore?.allModes?.some(m => m.name === mode.name)) {
                alert("이미 있는 모드입니다. 다른 모드명을 사용해주세요");
                return;
              }

              const response = await blockKitStore?.studioController.postModesStagingAsync(mode);
              if (response) {
                const copiedMode = {
                  ...response,
                  exposureCategoryIds: mode.exposureCategoryIds,
                  exposureLicenseIds: mode.exposureLicenseIds
                } as ModeJsonModel;
                await blockKitStore?.studioController.postExposureCategoriesStagingAsync(copiedMode);
                await blockKitStore?.studioController.postExposureLicensesStagingAsync(copiedMode);
              }
              await blockKitStore?.initModePageAsync();
              alert("복제 완료");
            }}
            onClickRemoveMode={async (mode: ModeJsonModel) => {
              if (!confirm(`${mode?.name}을 삭제하시겠습니까? 운영중인 키오스크에 문제가 발생할 수 있습니다.`)) {
                return;
              }

              const response = await blockKitStore?.studioController.deleteModeStagingAsync(mode);
              if (response.isSuccess) {
                await blockKitStore?.initModePageAsync();
              }
              alert(response.message);
            }}
            onClickSave={async (mode) => {
              if (!confirm("저장하시겠습니까?")) {
                return;
              }

              if (!mode) {
                alert("선택된 모드가 없습니다");
                return;
              }

              await blockKitStore?.studioController.postModeStagingAsync(mode);
              await blockKitStore?.studioController.postExposureCategoriesStagingAsync(mode);
              await blockKitStore?.studioController.postExposureLicensesStagingAsync(mode);
              await blockKitStore?.initModePageAsync();
              alert("저장 완료");
            }}
            onClickPublish={async () => {
              if (!confirm(t("publish-confirmation-message"))) {
                return;
              }

              if (!selectedMode) {
                alert("선택된 모드가 없습니다");
                return;
              }

              const response = await blockKitStore?.studioController.publishModeStagingAsync(selectedMode);
              if (response?.isSuccess) {
                await blockKitStore?.initModePageAsync();
                alert(t("publish-success"));
              } else {
                alert(`${t("publish-failed")} (${response?.message})`);
              }
            }}
          />
          <div className="border-start border-end border-bottom rounded">
            <Tabs defaultActiveKey={"category"}>
              <Tab eventKey={"category"} title={`카테고리 노출 목록(${selectedMode?.exposureCategoryIds?.length ?? 0})`}>
                <ModeSettingBox
                  exposureCategories={blockKitStore?.allCategories?.filter(category => selectedMode?.exposureCategoryIds?.includes(category?.id!)) ?? []}
                  listUpCategories={blockKitStore?.allCategories ?? []}
                  selectedExposureCategories={blockKitStore?.allCategories?.filter(i => selectedCategoryIds.includes(i.id!)) ?? []}
                  selectedListUpCategories={blockKitStore?.allCategories?.filter(i => selectedCategoryIds.includes(i.id!)) ?? []}
                  onChangeSelectedExposureCategories={(categories) => setSelectedCategoryIds(categories.map(c => c.id!))}
                  onChangeSelectedListUpCategories={(categories) => setSelectedCategoryIds(categories.map(c => c.id!))}
                  onClickAdd={async (categories) => {
                    if (!selectedMode) return;
                    const copiedSelectedMode = cloneDeep(selectedMode);

                    categories.forEach(category => {
                      if (!copiedSelectedMode.exposureCategoryIds?.includes(category.id!)) {
                        copiedSelectedMode.exposureCategoryIds?.push(category.id!);
                      }
                      if (!copiedSelectedMode.exposureCategories?.includes(category.name)) {
                        copiedSelectedMode.exposureCategories?.push(category.name);
                      }
                    });

                    setSelectedMode(copiedSelectedMode);
                    setSelectedCategoryIds([]);
                  }}
                  onClickRemove={async (categories) => {
                    if (!selectedMode) return;
                    const copiedSelectedMode = cloneDeep(selectedMode);

                    copiedSelectedMode.exposureCategoryIds = copiedSelectedMode.exposureCategoryIds?.filter(id => !categories.some(c => c.id === id));
                    copiedSelectedMode.exposureCategories = copiedSelectedMode.exposureCategories?.filter(name => !categories.some(c => c.name === name));

                    setSelectedMode(copiedSelectedMode);
                    setSelectedCategoryIds([]);
                  }}
                  onClickAddAll={() => {
                    if (selectedMode) {
                      const copiedSelectedMode = cloneDeep(selectedMode);
                      copiedSelectedMode.exposureCategories = blockKitStore?.allCategories?.map(category => category.name) ?? [];
                      copiedSelectedMode.exposureCategoryIds = blockKitStore?.allCategories?.map(category => category.id!) ?? [];
                      setSelectedMode(copiedSelectedMode);
                    }
                  }}
                  onClickRemoveAll={() => {
                    if (selectedMode) {
                      const copiedSelectedMode = cloneDeep(selectedMode);
                      copiedSelectedMode.exposureCategories = [];
                      copiedSelectedMode.exposureCategoryIds = [];
                      setSelectedMode(copiedSelectedMode);
                    }
                  }}
                />
              </Tab>
              <Tab eventKey={"license"} title={`라이선스 노출 목록(${selectedMode?.exposureLicenseIds?.length ?? 0})`}>
                <ModeSettingBoxWithLicense
                  exposureLicenses={blockKitStore?.allLicenses?.filter(license => selectedMode?.exposureLicenseIds?.includes(license?.id!)) ?? []}
                  listUpLicenses={blockKitStore?.allLicenses ?? []}
                  selectedExposureLicenses={blockKitStore?.allLicenses?.filter(i => selectedLicenseIds.includes(i.id!)) ?? []}
                  selectedListUpLicenses={blockKitStore?.allLicenses?.filter(i => selectedLicenseIds.includes(i.id!)) ?? []}
                  onChangeSelectedExposureLicenses={(licenses) => setSelectedLicenseIds(licenses.map(l => l.id!))}
                  onChangeSelectedListUpLicenses={(licenses) => setSelectedLicenseIds(licenses.map(l => l.id!))}
                  onClickAdd={(licenses) => {
                    if (!selectedMode) return;
                    const copiedMode = cloneDeep(selectedMode);

                    licenses.forEach(license => {
                      if (!copiedMode.exposureLicenseIds?.includes(license.id!)) {
                        copiedMode.exposureLicenseIds?.push(license.id!);
                      }
                      if (!copiedMode.exposureLicenses?.includes(license.name)) {
                        copiedMode.exposureLicenses?.push(license.name);
                      }
                    });

                    setSelectedMode(copiedMode);
                    setSelectedLicenseIds([]);
                  }}
                  onClickRemove={(licenses) => {
                    if (!selectedMode) return;
                    const copiedMode = cloneDeep(selectedMode);

                    copiedMode.exposureLicenseIds = copiedMode.exposureLicenseIds?.filter(
                      id => !licenses.some(l => l.id === id)
                    );
                    copiedMode.exposureLicenses = copiedMode.exposureLicenses?.filter(
                      name => !licenses.some(l => l.name === name)
                    );

                    setSelectedMode(copiedMode);
                    setSelectedLicenseIds([]);
                  }}
                  onClickAddAll={() => {
                    if (selectedMode) {
                      const copiedSelectedMode = cloneDeep(selectedMode);
                      copiedSelectedMode.exposureLicenseIds = [];
                      copiedSelectedMode.exposureLicenses = [];
                      blockKitStore?.allLicenses?.forEach(license => {
                        if (license.id && license.name) {
                          copiedSelectedMode.exposureLicenseIds?.push(license.id);
                          copiedSelectedMode.exposureLicenses?.push(license.name);
                        }
                      });
                      setSelectedMode(copiedSelectedMode);
                    }
                  }}
                  onClickRemoveAll={() => {
                    if (selectedMode) {
                      const copiedSelectedMode = cloneDeep(selectedMode);
                      copiedSelectedMode.exposureLicenseIds = [];
                      copiedSelectedMode.exposureLicenses = [];
                      setSelectedMode(copiedSelectedMode);
                    }
                  }}
                />
              </Tab>
            </Tabs>
          </div>
        </Col>
      </Row>
    </Container>
  )
}));

export default ModePage;